<template>
  <v-card class="pb-3">
    <v-card-title>
      <div class="text-h6 align-baseline text-center full-width">
        <span v-if="!editMode">
          Create a new order for
          <span class="text-h6">{{ selectedRecord.name }}</span>
        </span>
        <span v-else>
          Change existing order
          <span class="text-h6">{{ existingOrder.name }}</span>
        </span>
        <v-btn icon class="close-button elevation-0" @click="cancelDialog">
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text>
      <v-form ref="selectForm" v-model="isValid">
        <v-row>
          <v-col cols="9">
            <v-text-field
              autofocus
              label="Order name"
              v-model="orderName"
              required
              :rules="[() => !!orderName || 'Order name is required']"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-select
              :items="stateOptions"
              v-model="orderState"
              label="Set State"
            ></v-select>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12">
            <div class="d-flex align-center">
              <span>
                Use Event
                <span class="font-weight-bold">
                  {{ existingOrder.event }}
                </span>
              </span>
            </div>

            <div class="d-flex align-center">
              <v-autocomplete
                ref="eventSelect"
                v-model="newEventName"
                label="Select Event"
                hint="Select the Event to use for this Order"
                :items="eventList"
                clearable
                class="white--text mr-2"
                item-color="#78909c"
                clear-icon="mdi-close-circle"
                :search-input.sync="search"
                :disabled="useNewEvent !== eventType.USE_ANOTHER_EVENT"
              >
                <template v-slot:selection="{ attr, on, item, selected }">
                  <v-chip
                    v-bind="attr"
                    :input-value="selected"
                    color="#78909c"
                    class="white--text"
                    small
                    v-on="on"
                    close
                    @click:close="newEventName = ''"
                  >
                    <span v-text="item"></span>
                  </v-chip>
                </template>
              </v-autocomplete>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <div class="d-flex align-center">
              <v-text-field
                ref="calTextField"
                number
                v-model="formattedOrderExpiryDatetime"
                readonly
                @click:append-outer="showCal = true"
                @focus="showCal = true"
                append-outer-icon="mdi-calendar"
                label="Expiration Date"
                hint="Leave empty For Perpetual"
                persistent-hint
              ></v-text-field>
            </div>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <v-radio-group
              column
              v-model="orderFrequencyIsTrueCountIsFalse"
              id="expiry-event"
              class="mt-0"
            >
              <v-radio :value="true">
                <template v-slot:label>
                  <span class="d-flex flex-row justify-start align-end">
                    <v-text-field
                      :disabled="!orderFrequencyIsTrueCountIsFalse"
                      v-model="orderFreqCount"
                      type="number"
                      suffix="times per"
                      class="mr-5"
                      label="Frequency"
                    ></v-text-field>
                    <v-autocomplete
                      clearable
                      :items="freqPeriod"
                      item-text="name"
                      item-value="value"
                      :disabled="!orderFrequencyIsTrueCountIsFalse"
                      chips
                      deletable-chips
                      clear-icon="mdi-close-circle"
                      label="Period"
                      v-model="orderFrequency"
                      hide-selected
                      :search-input.sync="search"
                      @change="search = ''"
                      hint="Select the reporting period of this Order."
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              <p>
                                No Frequency matching "
                                <strong>{{ search }}</strong>
                                "
                              </p>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attr, on, item, selected }">
                        <v-chip
                          v-bind="attr"
                          :input-value="selected"
                          color="#78909c"
                          class="white--text"
                          v-on="on"
                          close
                          @click:close="orderFrequency = ''"
                        >
                          <span v-text="item.name ? item.name : item"></span>
                        </v-chip>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="item.name"
                          ></v-list-item-title>
                          <!-- <v-list-item-subtitle
                        v-text="item.symbol"
                      ></v-list-item-subtitle> -->
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </span>
                </template>
              </v-radio>

              <v-radio :value="false">
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <v-text-field
                      :disabled="orderFrequencyIsTrueCountIsFalse"
                      v-model="count"
                      type="number"
                      suffix="times in total"
                    ></v-text-field>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
          <v-dialog v-if="showCal" v-model="showCal" width="350" persistent>
            <div class="d-flex flex-row justify-center menu-background">
              <div class="d-flow flow-column mb-3">
                <v-date-picker
                  v-model="orderExpiryDatetime"
                  no-title
                  scrollable
                  show-adjacent-months
                ></v-date-picker>
                <div class="text-center">
                  <v-btn text color="secondary" @click="cancelCal">
                    Cancel
                  </v-btn>
                  <v-btn text color="primary" @click="showCal = false">
                    OK
                  </v-btn>
                </div>
              </div>
            </div>
          </v-dialog>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field
              label="Instructions"
              v-model="orderAdditionalInstructions"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col v-if="editMode">
            <v-btn
              text
              @click.stop="showNotifications = true"
              class="d-block ml-auto"
            >
              <v-icon small class="mr-2">mdi-cog</v-icon>
              Change Notifications
            </v-btn>
          </v-col>
          <v-col v-else>
            <span>Set</span>
            <v-btn text @click.stop="showNotifications = true">
              <v-icon small class="mr-2">mdi-cog</v-icon>
              Notifications
            </v-btn>
          </v-col>
          <v-dialog
            v-if="showNotifications"
            v-model="showNotifications"
            width="600"
            persistent
          >
            <AddEditNotifications
              :selectedRecord="selectedRecord"
              :notificationproto="notificationproto"
              :existingNotification="existingNotification"
              :orderId="editMode ? existingOrder._id : '-1'"
              :orderName="editMode ? existingOrder.name : orderName"
              :editMode="editMode"
              @cancel-this-dialog="showNotifications = !showNotifications"
              @close-this-dialog="showNotifications = !showNotifications"
              @update-notifications="changeNotifications"
            ></AddEditNotifications>
          </v-dialog>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="justify-space-between">
      <v-btn outlined @click="cancelDialog">Cancel</v-btn>
      <v-btn color="primary" @click="submitChanges" :disabled="!canSubmit">
        <v-progress-circular
          v-if="isSubmitting"
          class="mr-3"
          indeterminate
          :size="20"
          :width="1"
          color="secondary"
        ></v-progress-circular>
        {{ this.editMode ? "Update" : "Create" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters, mapActions, mapState } from "vuex";
import isDate from "lodash/isDate";
import moment from "moment";

// import { treeBuildCatList } from "../../../utils/treeUtils";
import AddEditNotifications from "./AddEditNotifications";
import { getOrderProto } from "../../../api";
import {
  createOrderForRecord,
  updateOrderForRecordById,
  createNotificationForOrder,
  updateNotificationForOrderById,
  deleteNotificationById,
} from "../../../utils/api";

export default {
  props: {
    selectedRecord: Object,
    tree: Object,
    events: Object,
    existingOrder: Object,
    editMode: Boolean,
    openNotifications: Boolean,
  },
  name: "AddEditOrder",
  components: { AddEditNotifications },

  data() {
    return {
      stateOptions: ["active", "inactive", "expired"],
      today: new Date().toISOString(),
      categories: [],
      selectedCategories: [],
      search: "",
      eventType: {
        USE_CURRENT_EVENT: 0,
        USE_ANOTHER_EVENT: 1,
        CREATE_NEW_EVENT: 2,
      },
      useNewEvent: 0,
      newEventName: null,
      isSubmitting: false,
      orderName: "",
      orderExpiryChoice: false,
      orderFrequencyIsTrueCountIsFalse: true,
      orderExpiryDatetime: null,
      count: "",
      complete: false,
      orderFrequency: "",
      orderFreqCount: 1,
      orderAdditionalInstructions: "",
      defaultNotification: true,
      showCal: false,
      showNotifications: false,
      isValid: false,
      orderState: "active",
      freqPeriod: [
        { name: "Day", value: "1" },
        { name: "Week", value: "7" },
        { name: "Month", value: "30" },
      ],
      updatedNotifications: [],
      order: {},
      existingNotification: undefined,
      notificationproto: {},
    };
  },
  async created() {
    this.order = await getOrderProto({ name: "" });
  },
  async mounted() {
    // this.categories = treeBuildCatList(this.categories, this.tree);
    this.useNewEvent = this.editMode
      ? this.eventType.USE_CURRENT_EVENT
      : this.eventType.USE_ANOTHER_EVENT;
    if (this.editMode) {
      // Populate the fields from the current order
      this.orderName = this.existingOrder.name;
      this.orderAdditionalInstructions = this.existingOrder.description;
      this.orderFrequency = this.existingOrder.freq_period;
      this.orderFreqCount = this.existingOrder.freq_count;
      this.orderState = this.existingOrder.state;
      this.getExpiryInfo(this.existingOrder.duration);
      // this.orderExpiryDatetime = this.updatedNotifications = cloneDeep(
      //   this.existingOrder.notifications
      // );
      // this.orderExpiryDatetime = this.existingOrder.expiration_date;
      this.defaultNotification = false;

      this.existingNotification = await this.$store.dispatch(
        "fetchExistingNotifications",
        this.existingOrder._id
      );
      if (this.existingNotification.length > 0) {
        this.existingNotification = this.existingNotification[0];
        this.existingNotification.order_id = this.existingOrder._id;
        this.existingNotification.notification_id = this.existingNotification._id;
      }
    }

    if (!this.editMode) {
      this.notificationproto = await this.$store.dispatch(
        "getNotificationProto"
      );
      this.notificationproto.acct_name = this.CurrentlySelectedAccount;
      this.notificationproto.order_id = this.order._id;
    }

    this.showNotifications = this.openNotifications;
    // new order get notification proto
  },
  computed: {
    ...mapGetters("allUoms", { units: "list" }, { root: true }),
    ...mapGetters({ CurrentlySelectedAccount: "getCurrentlySelectedAccount" }),
    canSubmit() {
      return true;
    },
    formattedOrderExpiryDatetime: {
      get: function() {
        return this.orderExpiryDatetime
          ? this.formatJustDatestamp(this.orderExpiryDatetime)
          : undefined;
      },
      set: function(newValue) {
        this.orderExpiryDatetime = newValue;
      },
    },
    eventList() {
      return Object.keys(this.events);
    },

    ...mapGetters({
      loginDetails: "getUser",
    }),
  },
  methods: {
    cancelDialog() {
      // Put up an "Are you sure?" dialog of some sort
      if (
        this.selectedCategories.length &&
        !window.confirm("Are you sure you want to cancel?")
      ) {
        return;
      }

      this.$refs.selectForm.reset();
      this.$emit("cancel-add-dialog");
    },
    getItemColor(item) {
      return item.name ? "#78909C" : "#039BE5";
    },
    cancelCal() {
      this.orderExpiryDatetime = "";
      this.showCal = false;
    },
    formatJustDatestamp(timestamp) {
      return moment(timestamp).format("MMM Do, YYYY");
    },
    computeDuration() {
      if (this.count) {
        return `${this.count} times`;
      } else {
        const start = moment(
          this.existingOrder?.created_at
            ? this.existingOrder.created_at
            : new Date(),
          "YYYY-MM-DD"
        ).startOf("day");
        const difference = moment(this.orderExpiryDatetime, "YYYY-MM-DD")
          .startOf("day")
          .from(start, true);
        return difference;
      }
    },
    getExpiryInfo(duration) {
      if (duration) {
        const pieces = duration.split(" ");
        if (duration.indexOf("times") === -1) {
          //next line is  const m = moment(creation, "YYYY-MM-DD"); trying duration
          const m = moment(duration, "YYYY-MM-DD");
          m.add(pieces[0], pieces[1]);
          this.orderExpiryChoice = false;
          this.orderExpiryDatetime = isDate(m) ? m.format("YYYY-MM-DD") : "";
        } else {
          this.count = pieces[0];
          this.orderExpiryChoice = true;
        }
      }
      return;
    },
    async submitChanges() {
      this.isSubmitting = true;
      if (
        this.useNewEvent === this.eventType.CREATE_NEW_EVENT &&
        this.selectedCategories.length
      ) {
        // Add this event name to all the selected nodes
        const promises = this.selectedCategories.map(async (item) => {
          return this.$store.dispatch("getRecordItemOnDemand", {
            recorditemid: item,
            recordid: this.selectedRecord._id,
          });
        });
        const nodes = await Promise.all(promises);
        nodes.forEach((item) => {
          if (Array.isArray(item.meta.events)) {
            const index = item.meta.events.indexOf(this.newEventName);
            if (index === -1) {
              // Only add the event if it isn't already there
              item.meta.events.push(this.newEventName);
            }
          } else {
            this.$set(item.meta, "events", [this.newEventName]);
          }
          this.updateNode(item, this.selectedRecord._id);
        });
        // const itWorked = await Promise.all(promises);
        // console.log(itWorked);
        this.$store.commit("UPDATE_EVENTS_GROUP", {
          event: this.newEventName,
          members: nodes,
          recordId: this.selectedRecord._id,
        });
        // console.log(itWorked);
        this.$store.dispatch("addNotification", {
          type: "success",
          message: `Created new Event ${this.newEventName}`,
        });
      }
      let response, orderId;
      if (this.editMode) {
        const { issuer, notifications, complete } = this.existingOrder;
        const params = {
          issuer,
          notifications,
          complete,
        };
        params.complete = this.complete;
        params.name = this.orderName;
        params.state = this.orderState;
        params.description = this.orderAdditionalInstructions;
        params.freq_period = this.orderFrequency;
        params.freq_count = this.orderFreqCount;
        // params.duration = this.computeDuration();
        params.duration = this.orderExpiryDatetime;
        params.event =
          this.useNewEvent === this.eventType.USE_CURRENT_EVENT
            ? this.existingOrder.event
            : this.newEventName;
        params.order_id = this.existingOrder._id;
        params.record_id = this.selectedRecord._id;
        params.expiration_date = this.orderExpiryDatetime;

        response = await updateOrderForRecordById(params);
        orderId = this.existingOrder._id;
      } else {
        const params = {
          _id: this.order._id,
          created_at: this.order.created_at,
          state: this.orderState,
          additional: this.order.additional,
          complete: this.complete,
          count: this.count,
          name: this.orderName,
          description: this.orderAdditionalInstructions,
          record_id: this.selectedRecord._id,
          issuer: this.loginDetails.username,
          freq_period: this.orderFrequency,
          freq_count: this.orderFreqCount,
          // duration: this.computeDuration(),
          duration: this.orderExpiryDatetime,
          notifications: [],
          event: this.newEventName,
          instructions: this.orderAdditionalInstructions,
        };
        // console.log("hello from add edit order payload is");
        // console.log(params);
        response = await createOrderForRecord(params);
      }
      if (!response.Error) {
        this.$store.dispatch("addNotification", {
          type: "success",
          message: `${this.editMode ? "Updated" : "Created new"} order ${
            this.orderName
          }`,
        });
        // We can't update or add the notifications until we have the order ID which we can now get out of the response
        // Get the order_id from the response
        orderId = response.Success._id;
      } else {
        this.$store.dispatch("addNotification", {
          type: "errorMessage",
          message: `Could not ${
            this.editMode ? "update" : "create new"
          } order ${this.orderName}. Error is ${response.Error}`,
        });
      }
      // If we have an orderId, we can now add/update notifications
      if (orderId) {
        if (this.defaultNotification) {
          // Create default notifications. Currently: notify provider once a week
          this.notificationproto.content = `Check on order ${this.orderName}`;
          this.notificationproto.action = "NEW";
          // const freqId = this.units.find((unit) => unit.name === "days");
          // const item = {
          //   record_id: this.selectedRecord._id,
          //   order_id: this.orderId,
          //   conditions: {
          //     count: "7",
          //     ctype: "frequency",
          //     when: {
          //       every: freqId,
          //     },
          //   },
          //   note: `Check on order ${this.orderName}`,
          //   recipients: ["me"],
          //   action: "NEW",
          // };
          this.updatedNotifications.push(this.notificationproto);
        }
        // Check if we have any changed notifications
        if (this.updatedNotifications.length) {
          // Use a for loop rather than map or forEach so the await will serialize the requests
          for (const notify of this.updatedNotifications) {
            let res;
            switch (notify.action) {
              case "DELETE":
                res = await deleteNotificationById(
                  this.selectedRecord._id,
                  notify._id
                );
                if (res.Error) {
                  this.$store.dispatch("addNotification", {
                    type: "errorMessage",
                    message: `Could not delete notification. Error is ${res.Error}`,
                  });
                }
                break;
              case "NEW":
                delete notify.action;
                notify.order_id = orderId;
                res = await createNotificationForOrder(notify);
                if (res.Error) {
                  this.$store.dispatch("addNotification", {
                    type: "errorMessage",
                    message: `Could not create notification. Error is ${res.Error}`,
                  });
                }
                break;
              case "EDIT":
                delete notify.action;
                notify.order_id = orderId;
                res = await updateNotificationForOrderById(notify);
                if (res.Error) {
                  this.$store.dispatch("addNotification", {
                    type: "errorMessage",
                    message: `Could not update notification. Error is ${res.Error}`,
                  });
                }
                break;
              default:
                break;
            }
          }
        }
      }
      if (!this.editMode) {
        await this.$store.dispatch(
          "postNotificationProto",
          this.notificationproto
        );
      } else {
        await this.$store.dispatch(
          "updateNotification",
          this.notificationproto
        );
      }
      // Now update the record with all the changed info
      await this.$store.dispatch("updateRecordFromDB", this.selectedRecord._id);

      this.isSubmitting = false;
      this.$emit("close-add-dialog");
    },
    async updateNode(obj, root_id) {
      const payload = {
        obj,
        root_id,
      };
      const response = await this.$store.dispatch(
        "updateRecNode/updateRecordNode",
        payload
      );
    },
    changeNotifications($event) {
      this.notificationproto = $event;
      this.showNotifications = false;
      // this.updatedNotifications = cloneDeep(updatedNotifications);
    },
  },
};
</script>

<style scoped lang="scss">
.close-button {
  position: absolute;
  right: 0px;
  cursor: pointer;
  z-index: 200;
  margin-top: -10px;
}
.v-stepper__content {
  padding-top: 0;
}
.full-width {
  width: 100%;
}
.menu-background {
  background-color: #fff;
}
</style>
<style lang="scss">
#select-event.v-input--radio-group__input {
  justify-content: space-between;
}
#expiry-event.v-input--radio-group__input {
  justify-content: space-between;
  flex-wrap: nowrap;
}
</style>
